import type { WhiteLabelPalette } from '../types'

// Exported for default-colors.ts, which disables the preview overrides while
// it reads the default palette.
export const PREVIEW_STYLE_ELEMENT_ID = 'sf-wl-preview-styles'
const LOGO_STYLE_ELEMENT_ID = 'sf-wl-preview-logo-styles'
const SESSION_KEY = 'sf_wl_preview'
const LOGO_SELECTOR = '[data-walkthrough-id="logo"] img'

const EMPTY_PALETTE: WhiteLabelPalette = {
  colorNames: [],
  sfColorNames: [],
  navigationAliases: {},
}

// The palette contract is owned by the server (WhiteLabel::Palette) and
// rendered as JSON on the preview trigger, so this preview and the production
// CSS export cannot drift apart. Fields are validated individually: JSON that
// parses but has the wrong shape must not crash buildCSS later.
export function parsePalette(raw: string | undefined): WhiteLabelPalette {
  if (!raw) return EMPTY_PALETTE
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_PALETTE

    const palette = parsed as Partial<WhiteLabelPalette>
    return {
      colorNames: Array.isArray(palette.colorNames) ? palette.colorNames : [],
      sfColorNames: Array.isArray(palette.sfColorNames)
        ? palette.sfColorNames
        : [],
      navigationAliases:
        typeof palette.navigationAliases === 'object' &&
        palette.navigationAliases !== null
          ? palette.navigationAliases
          : {},
    }
  } catch {
    return EMPTY_PALETTE
  }
}

// A logo loaded from a firm is a signed URL that expires after
// Firm::WL_LOGO_URL_EXPIRES_IN (one week). sessionStorage can
// easily outlive that in a long-lived tab, so we stop re-applying a stored URL
// well before its signature dies and fall back to the firm's real logo. The
// legacy `.navbar .brand` background image cannot report a load error, so this
// age check — not the `onerror` handler below — is the real guard.
// Uploaded logos are data URLs and never expire, so they are exempt.
const MAX_APPLIED_LOGO_AGE_MS = 6 * 24 * 60 * 60 * 1000

interface PreviewState {
  firmId: string
  colors: Record<string, string>
  logo: string | null
  logoSavedAt?: number
}

function isDataUrl(logo: string): boolean {
  return logo.startsWith('data:')
}

function readState(): PreviewState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as PreviewState
  } catch {
    return null
  }
}

// The colours come from other firms' palettes across stacks and from
// sessionStorage, so they are not trusted: a value like "red;} .x{...}" would
// smuggle extra rules into the style element. Structural characters are
// rejected outright, and where the browser can parse colours the value must
// also be a real colour (jsdom has no CSS global, hence the guard).
function isSafeColorValue(value: string): boolean {
  if (/[;{}]/.test(value)) return false
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function')
    return true

  return CSS.supports('color', value)
}

// Only keys in the palette contract are emitted, so an unknown key cannot be
// a second injection channel and the preview only ever overrides variables
// the production CSS export writes too.
function buildCSS(
  colors: Record<string, string>,
  palette: WhiteLabelPalette
): string {
  let vars = ''

  for (const [name, value] of Object.entries(colors)) {
    if (!value || !isSafeColorValue(value)) continue

    if (palette.sfColorNames.includes(name)) {
      vars += `--sf-color-${name}: ${value};`
    } else if (palette.colorNames.includes(name)) {
      vars += `--color-${name}: ${value};`
      const alias = palette.navigationAliases[name]
      if (alias) vars += `--sf-color-${alias}: ${value};`
    }
  }

  return vars ? `:root {${vars}}` : ''
}

// Keeps a <style> element in the head holding exactly `css`; empty css removes
// the element instead of leaving a stale override behind.
function upsertStyleElement(id: string, css: string): void {
  let el = document.getElementById(id) as HTMLStyleElement | null

  if (!css) {
    el?.remove()
    return
  }

  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }

  el.textContent = css
}

export function applyPreviewColors(
  colors: Record<string, string>,
  palette: WhiteLabelPalette
): void {
  upsertStyleElement(PREVIEW_STYLE_ELEMENT_ID, buildCSS(colors, palette))
}

function restoreOriginalSrc(img: HTMLImageElement): void {
  if (!img.dataset.wlOriginalSrc) return

  img.src = img.dataset.wlOriginalSrc
  delete img.dataset.wlOriginalSrc
}

export function applyPreviewLogo(logo: string | null): void {
  // Swap the navbar logo image (new navigation).
  document.querySelectorAll<HTMLImageElement>(LOGO_SELECTOR).forEach((img) => {
    img.onerror = null

    if (logo) {
      if (!img.dataset.wlOriginalSrc) img.dataset.wlOriginalSrc = img.src
      // An expired signature or a since-deleted blob would otherwise leave a
      // broken image in the navbar.
      img.onerror = (): void => {
        img.onerror = null
        restoreOriginalSrc(img)
      }
      img.src = logo
    } else {
      restoreOriginalSrc(img)
    }
  })

  // The legacy navbar renders the logo as a `.navbar .brand` background image.
  // Quotes and backslashes are escaped in one pass so the URL cannot terminate
  // the url() literal — escaping quotes alone is defeatable, because a
  // trailing \' would become \\' and leave the quote live. Matches what
  // white_label_helper.rb does for the real logo.
  const css = logo
    ? `.navbar .brand {background-image: url('${logo.replace(
        /['\\]/g,
        (c) => `\\${c}`
      )}');}`
    : ''
  upsertStyleElement(LOGO_STYLE_ELEMENT_ID, css)
}

export function savePreview(
  firmId: string,
  colors: Record<string, string>,
  logo: string | null
): void {
  try {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ firmId, colors, logo, logoSavedAt: Date.now() })
    )
  } catch {
    // Quota exceeded (e.g. a large logo) — keep the in-memory preview and skip
    // persistence so the session preview still applies until the next reload.
  }
}

export function loadPreviewColors(
  firmId: string
): Record<string, string> | null {
  const state = readState()
  return state && state.firmId === firmId ? state.colors : null
}

export function loadPreviewLogo(firmId: string): string | null {
  const state = readState()
  if (!state || state.firmId !== firmId) return null

  const logo = state.logo ?? null
  if (!logo || isDataUrl(logo)) return logo

  const age = Date.now() - (state.logoSavedAt ?? 0)
  return age > MAX_APPLIED_LOGO_AGE_MS ? null : logo
}
